import ApiUserRoles, { SystemUserRoles } from '@/entities/enums/UserRoles';
import LocalStorageKeys from '@/entities/enums/LocalStorageKeys';
import SessionStorage from '@/services/repository/SessionStorage';
import CookiesStorage from './CookiesStorage';
import { vxm } from '@/store/store.vuex';
import { AdminDepartment } from '@/entities/models/AdminDepartment';
import { CustomerPermissions } from './permissions/CustomerPermissions';
import { setFaroUser } from './logging/FaroLogger';
var vxDepartmentStore = vxm.departmentStore;
//To avoid performance issues, create Set of customer permission globally (required when checking permissions for admin)
const customerPermissionsSet = new Set(Object.values(CustomerPermissions));
export default class LoggedUserManager {
    /**
     * Saves login credentials after login. These credentials are used to authorize all API calls.
     * @param username
     * @param sessionPassword
     * @param apiUserFullName
     */
    static saveLoginData(username, sessionPassword, apiUserFullName) {
        CookiesStorage.storeData(CookiesStorage.keys.USERNAME, username, Infinity);
        CookiesStorage.storeData(CookiesStorage.keys.SESSION_PASSWORD, sessionPassword, Infinity);
        CookiesStorage.storeData(CookiesStorage.keys.API_USER_FULL_NAME, apiUserFullName, Infinity);
        CookiesStorage.removeData(CookiesStorage.keys.IN_COMPANY);
    }
    /**
     * Saves ID or currently logged user.
     * @param userId Id of current user
     */
    static saveIdOfLoggedUser(userId) {
        CookiesStorage.storeData(CookiesStorage.keys.API_USER_ID, userId, Infinity);
        setFaroUser(userId, vxDepartmentStore?.selectedDepartment?.id);
    }
    /**
     * Load ID or currently logged user.
     */
    static getIdOfLoggedUser() {
        return CookiesStorage.loadData(CookiesStorage.keys.API_USER_ID);
    }
    /**
     * Backups admin login credentials before login into company.
     * @param department Company user wants to log in
     */
    static loginToCompany(department) {
        this.setSelectedDepartment(department);
        CookiesStorage.storeData(CookiesStorage.keys.IN_COMPANY, 'true', Infinity);
    }
    /**
     * Reloads admin login credentials from backup. Used when admin log out from company. Backup is deleted after reload.
     */
    static reloadAdminLoginData() {
        const apiUserRole = CookiesStorage.loadData(CookiesStorage.keys.API_USER_ROLE);
        let permissions = [];
        this.setPermissions(permissions);
        CookiesStorage.storeData(CookiesStorage.keys.API_USER_ROLE, apiUserRole, Infinity);
        CookiesStorage.removeData(CookiesStorage.keys.IN_COMPANY);
    }
    /**
     * Delete login credentials
     */
    static deleteLoginData() {
        CookiesStorage.removeData(CookiesStorage.keys.USERNAME);
        CookiesStorage.removeData(CookiesStorage.keys.SESSION_PASSWORD);
        CookiesStorage.removeData(CookiesStorage.keys.API_USER_FULL_NAME);
        CookiesStorage.removeData(CookiesStorage.keys.API_USER_ROLE);
        CookiesStorage.removeData(CookiesStorage.keys.API_USER_ID);
        CookiesStorage.removeData(CookiesStorage.keys.IN_COMPANY);
        CookiesStorage.removeData(LocalStorageKeys.PERMISSIONS);
        localStorage.removeItem(LocalStorageKeys.PERMISSIONS);
        SessionStorage.clearStorageForSession();
    }
    /**
     * Delete admin login credentials
     */
    static deleteAdminLoginData() {
        CookiesStorage.removeData(CookiesStorage.keys.API_USER_ROLE);
        localStorage.removeItem(LocalStorageKeys.ADMIN_PERMISSIONS);
    }
    /**
     * Check if cookies contain login credentials
     * @returns true if login credentials exists.
     */
    static storageContainLoginCredentials() {
        return (CookiesStorage.exists(CookiesStorage.keys.USERNAME) && CookiesStorage.exists(CookiesStorage.keys.SESSION_PASSWORD));
    }
    /**
     * Check if user is system admin or reseller admin
     * @returns true if logged user is system admin.
     */
    static isAdmin() {
        //todo check all isadmin roles not only these two
        return (vxDepartmentStore.selectedSystemRoleKey && vxDepartmentStore.selectedSystemRoleKey !== SystemUserRoles.CUSTOMER);
    }
    /**
     * Check if user is system admin
     * @returns true if logged user is system admin.
     */
    static isSystemAdmin() {
        return vxDepartmentStore.selectedSystemRoleKey === SystemUserRoles.SYSTEM_ADMIN;
    }
    /**
     * Check if user is reseller admin
     * @returns true if logged user is reseller admin.
     */
    static isResellerAdmin() {
        return vxDepartmentStore.selectedSystemRoleKey === SystemUserRoles.RESELLER_ADMIN;
    }
    /**
     * Check if user is department admin or owner
     * @returns true if logged user is department admin or owner.
     */
    static isDepartmentAdminOrOwner() {
        return (ApiUserRoles.COMPANY_OWNER == vxDepartmentStore.selectedDepartmentRoleKey ||
            ApiUserRoles.COMPANY_ADMIN == vxDepartmentStore.selectedDepartmentRoleKey);
    }
    /**
     * Check if admin is logged in company
     * @returns true if admin is logged into company.
     */
    static isLoggedToCompany() {
        return CookiesStorage.loadData(CookiesStorage.keys.IN_COMPANY) == 'true';
    }
    static setSelectedDepartment(value) {
        vxDepartmentStore.selectedDepartment = AdminDepartment.convertToUserDepartment(value);
    }
    static getPermissions() {
        return vxDepartmentStore.selectedDepartmentPermissions;
    }
    static getRole() {
        return vxDepartmentStore.selectedSystemRoleKey;
    }
    static setPermissions(value) {
        vxDepartmentStore.selectedDepartmentPermissions = value;
    }
    /**
     * Checks permissions of currently logged user
     * @param permissionToCheck
     * @returns true if user has given permission, false otherwise
     */
    static checkPermissions(permissionToCheck) {
        const permissions = LoggedUserManager.getPermissions(); // permissions of current user
        //system admin can do everything
        if (LoggedUserManager.isSystemAdmin())
            return true;
        else if (LoggedUserManager.isAdmin()) {
            //for admin user roles, check only admin permissions. If given permission is customer permission, always allow it
            const isCustomerPermission = customerPermissionsSet.has(permissionToCheck);
            if (isCustomerPermission)
                return true;
        }
        //check list of permissions for customer or if given permission is admin permission and user is admin.
        if (!permissions)
            return false;
        else
            return permissions.includes(permissionToCheck);
    }
    static setDepartmentRole(value) {
        vxDepartmentStore.selectedDepartmentRoleKey = value;
    }
    static setRole(value) {
        vxDepartmentStore.selectedSystemRoleKey = value;
    }
    static clearDepartments() {
        vxDepartmentStore.departments = null;
        vxDepartmentStore.selectedDepartment = null;
    }
    static isHandlingExpiration = false;
    static startExpirationHandling() {
        if (this.isHandlingExpiration)
            return false;
        this.isHandlingExpiration = true;
        return true;
    }
    static resetExpirationHandling() {
        this.isHandlingExpiration = false;
    }
}
